import React, { useState } from 'react';
import SVG from 'react-inlinesvg';
import { Skeleton } from 'antd';
import { MediaHelpers } from '@wajeez-web/core/api';
import { ImSpinner8 } from 'react-icons/im';
import { useCommonSelector } from '@wajeez-web/stores/common';

import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

import {
  BaseComponentProps,
  useComponentData,
  useSkeletonHelper,
} from '../helpers/contentHooks';
import { ClickableComponentLayout } from '../layouts/ClickableComponentLayout';

export interface CategoriesSeeAllListParameters {
  title: string | null;
  seeAllTitle: string | null;
  withProgress: 'False' | 'True';
}

export function CategoriesSeeAllList({
  component,
  context,
  isLastSibling,
}: BaseComponentProps) {
  const [pageNumber, setPageNumber] = useState(1);

  const { contents, contentsRequestStatus, meta } =
    useComponentData<CategoriesSeeAllListParameters>(
      component,
      context,
      pageNumber
    );

  const { isActive } = useSkeletonHelper(
    contentsRequestStatus,
    contents?.length ? true : false
  );

  const { lastElementRef } = useInfiniteScroll(
    meta?.hasNextPage,
    setPageNumber
  );

  const isContentOrLoading = contents?.length;

  return isContentOrLoading ? (
    <>
      <div
        className={`w-full ${
          !isLastSibling ? 'border-b-[0.5px]' : 'pb-[20px]'
        } border-[#C6C6C8] dark:border-dark-divider pl-4 pr-[6px] sm:pr-[56px] grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-2 sm:gap-x-[51px] sm:gap-y-[52px]`}
      >
        {contents?.map((content, i) => (
          <CategoriesSeeAllListItem
            ref={contents?.length - 1 === i ? lastElementRef : null}
            key={content.id}
            component={{ ...component?.itemTemplate, uniqueId: i }}
            context={content}
          />
        ))}
      </div>
      {contentsRequestStatus === 'loading' && (
        <div className="w-full pt-2 text-[24px] text-primary dark:text-primary-dark flex justify-center items-center">
          <ImSpinner8 className="animate-spin mx-auto" />
        </div>
      )}
    </>
  ) : (
    <CategoriesSeeAllListSkeleton
      isLastSibling={isLastSibling}
      isActive={isActive}
    />
  );
}

const CategoriesSeeAllListSkeleton = ({
  isLastSibling,
  isActive = true,
}: {
  isLastSibling: boolean | 0 | undefined;
  isActive?: boolean;
}) => {
  return (
    <div
      className={`w-full ${
        !isLastSibling ? 'border-b-[0.5px]' : 'pb-[20px]'
      } border-[#C6C6C8] dark:border-dark-divider pl-4 pr-[6px] sm:pr-[56px] grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-x-2 sm:gap-x-4 sm:gap-y-[102px]`}
    >
      {Array.from({ length: 10 }).map((_, i) => {
        return (
          <Skeleton.Node
            key={i}
            active={isActive}
            className="!h-[120px] sm:!h-[100.5px] sm:!w-[150px] !w-full mb-4 dark:bg-dark-medium"
          >
            <div />
          </Skeleton.Node>
        );
      })}
    </div>
  );
};

export interface CategoriesSeeAllListItemParameters {
  backgroundImage: string | null;
  icon: string | null;
}

export const CategoriesSeeAllListItem = React.forwardRef<
  HTMLDivElement,
  BaseComponentProps
>(({ component, context }, ref) => {
  const theme = useCommonSelector(({ common }) => common.settings.theme);

  const { getParameterValue, content, isLockedContent } =
    useComponentData<CategoriesSeeAllListItemParameters>(component, context);

  const backgroundImage = getParameterValue('backgroundImage');
  const icon = getParameterValue('icon');

  return (
    <ClickableComponentLayout content={content}>
      <div
        ref={ref}
        style={{
          backgroundImage:
            'url(' + MediaHelpers.getPublicMediaById(backgroundImage) + ')',
        }}
        className={`relative h-[120px] sm:h-[100.5px] sm:w-[150px] w-full border rounded-[10px] bg-cover bg-center bg-no-repeat mb-[4px] flex justify-center items-center`}
      >
        {theme === 'dark' && (
          <div className="absolute top-0 left-0 w-full h-full bg-[#00000033]" />
        )}
        {isLockedContent && (
          <div className="w-[31px] h-[31px] absolute bottom-[10px] left-[10px]">
            <img
              src="../../assets/images/lock.png"
              alt="lock"
              className="block w-full h-full"
            />
          </div>
        )}
        <div className="w-[40px] sm:w-[36px] h-[40px] sm:h-[42.75px]">
          <SVG
            src={MediaHelpers.getPublicMediaById(icon)}
            preProcessor={(code: string) =>
              code
                .replace(/fill=".*?"/g, `fill="white"`)
                .replace(/stroke=".*?"/g, `stroke="white"`)
            }
            width={'100%'}
            height={'100%'}
          />
        </div>
        <div className="sm:hidden absolute right-[8px] bottom-[8px] text-[13px] text-white">
          {content?.title}
        </div>
      </div>
      <div className="hidden sm:block text-[18px] font-normal text-black">
        {content?.title}
      </div>
    </ClickableComponentLayout>
  );
});
